import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HeroSection from './HeroSection';
import FleetSection from './FleetSection';
import ServicesSection from './ServicesSection';
import ReviewsSection from './ReviewsSection';
import Navbar from './Navbar';
import Footer from './Footer';

interface CityPageProps {
  city: string;
  state: string;
  stateAbbr: string;
  nearbyCities: Array<{ name: string; path: string }>;
  airportName: string;
  fboName: string;
  longDistanceRoutes: Array<{ from: string; to: string }>;
}

const CityPageTemplate = ({
  city,
  state,
  stateAbbr,
  nearbyCities,
  airportName,
  fboName,
  longDistanceRoutes,
}: CityPageProps) => {
  const cityState = `${city}, ${stateAbbr}`;
  const pageUrl = `/chauffeur-${city.toLowerCase()}-${stateAbbr.toLowerCase()}`;
  const pageTitle = `Best Chauffeur Service in ${cityState} | FBO, Hourly, Executive | TiLimousine`;
  const metaDescription = `Book luxury chauffeur service in ${cityState} with TiLimousine. Private jet pickup, executive black car hire, hourly drivers, and airport transfers with luggage help.`;

  // Custom hero content for the city
  const heroContent = {
    title: `Premium Chauffeur Service in ${cityState}`,
    subtitle: `Experience luxury transportation in ${city} with our professional chauffeurs and premium fleet.`,
    ctaText: 'Book Your Ride',
    ctaLink: '/contact'
  };

  // Custom services content for the city
  const servicesContent = {
    title: `Our Services in ${cityState}`,
    services: [
      {
        title: 'Airport Transportation',
        description: `Reliable black car service to and from ${airportName} with professional chauffeurs.`,
        icon: 'plane'
      },
      {
        title: 'FBO & Private Jet Service',
        description: `Premium transportation to and from ${fboName} for private jet travelers.`,
        icon: 'jet'
      },
      {
        title: 'Corporate Transportation',
        description: `Executive car service for business meetings and corporate events in ${city}.`,
        icon: 'briefcase'
      },
      {
        title: 'Hourly Service',
        description: `Flexible hourly chauffeur service throughout ${cityState} for all your needs.`,
        icon: 'clock'
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://tilimousine.com${pageUrl}`} />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "TiLimousine ${city} Chauffeur Service",
            "url": "https://tilimousine.com${pageUrl}",
            "description": "Premium chauffeur service in ${cityState} offering private jet pickup, executive transportation, hourly service, and airport transfers.",
            "areaServed": [
              {
                "@type": "City",
                "name": "${city}",
                "containedIn": "${state}"
              },
              {
                "@type": "State",
                "name": "${state}"
              }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "${city}",
              "addressRegion": "${stateAbbr}"
            },
            "serviceType": [
              "Executive Chauffeur Service",
              "Private Jet Transportation",
              "Airport Transfer Service",
              "Hourly Chauffeur Service",
              "Long-Distance Transportation"
            ]
          }
          `}
        </script>
      </Helmet>

      <Navbar />
      
      <HeroSection 
        title={heroContent.title}
        subtitle={heroContent.subtitle}
        ctaText={heroContent.ctaText}
        ctaLink={heroContent.ctaLink}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-8">Why Choose TiLimousine in {cityState}?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Professional Service</h3>
              <p>Licensed and experienced chauffeurs providing premium transportation in {city}.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Luxury Fleet</h3>
              <p>Modern, well-maintained vehicles for a comfortable ride in {cityState}.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">24/7 Availability</h3>
              <p>Round-the-clock service for all your transportation needs in {city}.</p>
            </div>
          </div>
        </div>
      </div>

      <FleetSection />

      <ServicesSection 
        title={servicesContent.title}
        services={servicesContent.services}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-8">Popular Routes from {city}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {longDistanceRoutes.map((route, index) => (
              <div key={index} className="bg-gray-900 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gold mb-4">
                  {route.from} to {route.to}
                </h3>
                <p>Premium transportation between major cities with professional chauffeurs.</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {nearbyCities.length > 0 && (
        <div className="container mx-auto px-4 py-16 bg-gray-900">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-3xl font-bold text-white mb-8">Chauffeur Service in Nearby Cities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {nearbyCities.map((nearbyCity, index) => (
                <Link 
                  key={index}
                  to={nearbyCity.path}
                  className="text-gold hover:text-gold/80 transition-colors p-4 bg-gray-800 rounded-lg"
                >
                  {nearbyCity.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <ReviewsSection />

      <div className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-8">Book Your {city} Chauffeur Service</h2>
          <p className="text-xl mb-8">
            Experience the best chauffeur service in {cityState}. Contact us today to book your luxury transportation.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-gold text-black px-8 py-3 rounded-full hover:bg-gold/80 transition-colors"
          >
            Book Now
          </Link>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default CityPageTemplate; 